/**
 * SED-FOUNDATION — pure projection of `SED.workflows` into per-section
 * and whole-SED summaries for read-only surfacing (CoverageMatrix).
 *
 * No mutation. Legacy SEDs (workflows === undefined) yield empty summaries.
 */
import type { EvidenceWorkflowItem, SitingNoteEntry } from '../../types/siteEvidence';

export interface WorkflowSectionSummary {
  /** Section id (first dot-segment of `sedFieldPath`). */
  sectionId: string;
  /** Open (unresolved) open-question items. */
  openQuestions: number;
  /** Confirmed verification-status items WITH evidence. */
  verifiedConfirmed: number;
  /** Verification-status items that are pending / awaiting evidence. */
  verificationPending: number;
  /** Siting-note items attached to the section. */
  sitingNotes: number;
}

export interface WorkflowSummary {
  totalOpenQuestions: number;
  totalVerifiedConfirmed: number;
  totalVerificationPending: number;
  totalSitingNotes: number;
  /** Per-section breakdown keyed by section id. */
  bySection: Record<string, WorkflowSectionSummary>;
  /** True when the SED carries no workflow items (legacy or empty). */
  isEmpty: boolean;
}

/** The leading section id of an item's `sedFieldPath` (e.g. `grid`). */
export function workflowSectionId(item: Pick<EvidenceWorkflowItem, 'sedFieldPath'>): string {
  const path = item.sedFieldPath ?? '';
  const dot = path.indexOf('.');
  return dot >= 0 ? path.slice(0, dot) : path;
}

function blankSection(sectionId: string): WorkflowSectionSummary {
  return {
    sectionId,
    openQuestions: 0,
    verifiedConfirmed: 0,
    verificationPending: 0,
    sitingNotes: 0,
  };
}

/**
 * Project a SED's workflow items into a read-only summary. Items with a
 * `'superseded'` or `'dismissed'` status are excluded from the live counts
 * (they are historical). Pure + deterministic.
 */
export function summarizeWorkflows(
  workflows: readonly EvidenceWorkflowItem[] | undefined,
): WorkflowSummary {
  const summary: WorkflowSummary = {
    totalOpenQuestions: 0,
    totalVerifiedConfirmed: 0,
    totalVerificationPending: 0,
    totalSitingNotes: 0,
    bySection: {},
    isEmpty: true,
  };

  for (const item of workflows ?? []) {
    if (item.status === 'superseded' || item.status === 'dismissed') continue;
    summary.isEmpty = false;
    const sectionId = workflowSectionId(item);
    const section = summary.bySection[sectionId] ?? blankSection(sectionId);

    switch (item.kind) {
      case 'open-question':
        if (item.status === 'open' || item.status === 'pending') {
          section.openQuestions += 1;
          summary.totalOpenQuestions += 1;
        }
        break;
      case 'verification-status':
        if (item.status === 'confirmed') {
          section.verifiedConfirmed += 1;
          summary.totalVerifiedConfirmed += 1;
        } else {
          section.verificationPending += 1;
          summary.totalVerificationPending += 1;
        }
        break;
      case 'siting-note': {
        const noteCount = countSitingNotes(item.sitingNotes);
        section.sitingNotes += noteCount;
        summary.totalSitingNotes += noteCount;
        break;
      }
    }

    summary.bySection[sectionId] = section;
  }

  return summary;
}

function countSitingNotes(notes: SitingNoteEntry[] | undefined): number {
  return notes?.length ?? 0;
}

/**
 * Roll up the per-section summaries for a set of section ids (e.g. a
 * requirement's `sedSectionsHelpful`). Returns a flat summary the row chip
 * can render. Pure.
 */
export function rollupSectionsForRequirement(
  summary: WorkflowSummary,
  sectionIds: readonly string[],
): WorkflowSectionSummary {
  const out = blankSection('__rollup__');
  for (const id of sectionIds) {
    const s = summary.bySection[id];
    if (!s) continue;
    out.openQuestions += s.openQuestions;
    out.verifiedConfirmed += s.verifiedConfirmed;
    out.verificationPending += s.verificationPending;
    out.sitingNotes += s.sitingNotes;
  }
  return out;
}
